import React, { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import {
    TransportOrderDataForm,
    TransportOrderOptionTrip
} from '../../../app/models/TransportOrder';
import useTransportOrderService from '../../../app/services/hooks/useTransportOrderService';
import TransportOrderForm from './forms/TransportOrderForm';
import SelectTwo, { SelectTwoMapperOptions } from '../../../components/SelectTwo';
import { RouteSegment } from '../../../app/models/RouteSegment';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';
import { OptionTrip } from '../../../app/shared/enums';
import TransportOrderMultiForm from './forms/TransportOrderMultiForm';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';

interface Props {
    workOrderId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
    truckId: number;
}

const TransportOrderMultiCreate = ({ onSaved, onCancel, onError, workOrderId, truckId }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { createTransportOrder, multiStoreTransportOrder, fetchingCreateTransportOrder, fetchingStoreTransportOrder, getTransportOrderPositionInfo } =
        useTransportOrderService();

    const [templateData, setTemplateData] = useState<any>(null);
    const [transportOrders, setTransportOrders] = useState<TransportOrderDataForm[]>([]);
    const [errorFields, setErrorFields] = useState<any>({});
    const [position, setPosition] = useState<number>(1);
    const [selectedTransportOrderId, setSelectedTransportOrderId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<number>(0);

    const { getInverseRouteSegments, fetchingGetInverseRouteSegmentByRouteSegments } = useRouteSegmentService();
    const [inverseRouteSegmentsList, setInverseRouteSegmentsList] = useState<RouteSegment[][]>([]);
    const routeSegmentIdSelectedList = useRef<number[]>([]);

    const [beforeSegment, setBeforeSegment] = useState<TransportOrderDataForm | null>(null);
    const [afterSegment, setAfterSegment] = useState<TransportOrderDataForm | null>(null);


    const [sharedFields, setSharedFields] = useState({
        worker_id: 0,
        hopper_id: 0,
        truck_id: truckId
    });

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createTransportOrder(workOrderId, {
            onSuccess: (response: ServiceResponse) => {
                setTemplateData(response.data);
                addNewSegment(); // Inicializar con 1 formulario
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        }, { mode: 'multi' } 
    );
    }, []);

    const removeSegment = (index: number) => {
        setTransportOrders(prev => prev.filter((_, i) => i !== index));
    };

    const updateSegment = (index: number, data: TransportOrderDataForm) => {
        const newList = [...transportOrders];
        newList[index] = data;
        setTransportOrders(newList);
    };

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando múltiples tramos...');

        multiStoreTransportOrder(
            {
                work_order_id: workOrderId,
                orders: transportOrders.map((order, index) => ({
                    ...order,
                        truck_id: sharedFields.truck_id,
                        worker_id: sharedFields.worker_id,
                        hopper_id: sharedFields.hopper_id,
                        position: position + index,
                }))
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    toast.success(response.message);
                    if (onSaved) onSaved();
                },
                onError: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    useReactConfirmAlert().errorAlert({
                        title: 'Error',
                        message: response.message
                    });
                    /* if (onError) onError(); */
                },
                onFieldError: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    setErrors(response.data); 
                }
            }
        );
    };


    const cancel = () => {
        if (onCancel) onCancel();
    };

    const addNewSegment = () => {
        setTransportOrders(prev => {
            const updated = [
                ...prev,
                {
                    work_order_id: workOrderId,
                    truck_id: sharedFields.truck_id,
                    is_type_sell: true,
                    client_id: 0,
                    product_id: 0,
                    worker_id: sharedFields.worker_id,
                    hopper_id: sharedFields.hopper_id,
                    route_segment_id: 0,
                    option_trip: OptionTrip.ONE_WAY,
                }
            ];
            // Agregar nueva entrada vacía en las rutas inversas
            setInverseRouteSegmentsList(prevInverse => [...prevInverse, []]);

            // Agregar valor inicial 0 para la referencia del ID de tramo
            routeSegmentIdSelectedList.current.push(0);
            // Actualizamos la pestaña activa fuera del setState para evitar conflictos con el estado anterior
            setTimeout(() => setActiveTab(updated.length - 1), 0);
            return updated;
        });
    };

    const [positionContext, setPositionContext] = useState<{
        before?: any;
        after?: any;
    } | null>(null);


    useEffect(() => {
        transportOrders.forEach((order, index) => {
            const currentId = order.route_segment_id;
            const prevId = routeSegmentIdSelectedList.current[index];

            if (currentId && currentId !== prevId) {
                getInverseRouteSegments(currentId, {
                    onSuccess: (response: ServiceResponse) => {
                        const updatedOrders = [...transportOrders];
                        updatedOrders[index] = {
                            ...updatedOrders[index],
                            return_route_segment_id:
                                response.data.inverse_route_segments.length > 0
                                    ? response.data.inverse_route_segments[0].id
                                    : 0,
                            external_rate: response.data.route_segment.external_rate,
                            currency_external_rate_id: response.data.route_segment.currency_external_rate_id ?? undefined,
                            unit_external_rate_id: response.data.route_segment.unit_external_rate_id ?? undefined,
                            commission: response.data.route_segment.commission ?? undefined,
                            currency_commission_id: response.data.route_segment.currency_commission_id ?? undefined,
                            unit_commission_id: response.data.route_segment.unit_commission_id ?? undefined
                        };
                        setTransportOrders(updatedOrders);

                        // Actualiza rutas inversas
                        const newInverseList = [...inverseRouteSegmentsList];
                        newInverseList[index] = response.data.inverse_route_segments;
                        setInverseRouteSegmentsList(newInverseList);

                        routeSegmentIdSelectedList.current[index] = currentId;

                        if (
                            updatedOrders[index].option_trip === OptionTrip.ROUND_TRIP &&
                            response.data.inverse_route_segments.length === 0
                        ) {
                            useReactConfirmAlert().errorAlert({
                                title: 'Error',
                                message:
                                    'No se encontró ninguna opción ruta de vuelta para la opción de ruta seleccionada. Debe crearla en el módulo de tramos.'
                            });
                        }
                    },
                    onError: () => {
                        const updatedOrders = [...transportOrders];
                        updatedOrders[index].route_segment_id = prevId;
                        setTransportOrders(updatedOrders);
                    }
                });
            }
        });
    }, [transportOrders]);



    if (!templateData || fetchingCreateTransportOrder) return <LazyLoading height={300} />;

    return (
        <>
        <div className="mb-4">
            <div className="mb-3">
                <div className='row col-md-6'>
                    <label htmlFor="referenceOrder" className="form-label">
                        Selecciona orden de referencia
                    </label>
                </div>

                <div className='row col-12'>
                    <div className="col-8">
                        <SelectTwo
                            name="referenceOrder"
                            id="referenceOrder"
                            inputValue={selectedTransportOrderId}
                            options={templateData?.transport_orders ? 
                                templateData.transport_orders.map((to: any) => {
                                    const origin = to.route_segment?.origin || '';
                                    const destiny = to.route_segment?.destiny || '';
                                    const tramCode = to.route_segment?.id || '';
                                    return {
                                        value: to.id,
                                        label: `Orden ${to.id} - Tramo [${tramCode}]: ${origin} → ${destiny}`
                                    };
                                }) 
                                : []
                            }
                            onChange={(value: any) => {
                                const selectedId = Number(value.value);
                                setSelectedTransportOrderId(selectedId);
                                // Encuentra la posición para insertar después
                                const selectedOrder = templateData?.transport_orders?.find((to: any) => to.id === selectedId);
                                if (selectedOrder) {
                                    setPosition(selectedOrder.position + 1);
                                }
                            }}
                            placeholder={'Seleccione Orden de Transporte'}
                        />
                    </div>
                    <div className="col-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                if (!selectedTransportOrderId) {
                                    toast.warn('Debes seleccionar una orden de transporte');
                                    return;
                                }
                                
                                // Encontrar la orden seleccionada por ID
                                const selectedOrder = templateData?.transport_orders?.find((to: any) => to.id === selectedTransportOrderId);
                                
                                if (selectedOrder) {
                                    // Convertir la orden a TransportOrderDataForm para mostrar como referencia
                                    const referenceSegment: TransportOrderDataForm = {
                                        ...selectedOrder,
                                        work_order_id: workOrderId,
                                    };
                                    
                                    // Encontrar la orden siguiente por ID (usando next_transport_order_id si existe, si no por posición)
                                    let nextSegment = null;
                                    if (selectedOrder.next_transport_order_id) {
                                        const nextOrder = templateData?.transport_orders?.find((to: any) => to.id === selectedOrder.next_transport_order_id);
                                        nextSegment = nextOrder ? {
                                            ...nextOrder,
                                            work_order_id: workOrderId,
                                        } : null;
                                    } else {
                                        // Fallback: buscar por posición si no hay referencia
                                        const nextOrder = templateData?.transport_orders?.find((to: any) => to.position === selectedOrder.position + 1);
                                        nextSegment = nextOrder ? {
                                            ...nextOrder,
                                            work_order_id: workOrderId,
                                        } : null;
                                    }
                                    
                                    setBeforeSegment(referenceSegment);
                                    setAfterSegment(nextSegment);
                                    
                                    // Establecer la posición para insertar después de la orden seleccionada
                                    setPosition(selectedOrder.position + 1);

                                    setTransportOrders([
                                        {
                                            work_order_id: workOrderId,
                                            truck_id: sharedFields.truck_id,
                                            is_type_sell: true,
                                            client_id: 0,
                                            product_id: 0,
                                            worker_id: sharedFields.worker_id,
                                            hopper_id: sharedFields.hopper_id,
                                            route_segment_id: 0,
                                            option_trip: OptionTrip.ONE_WAY,
                                        }
                                    ]);
                                } else {
                                    toast.error('No se encontró la orden seleccionada');
                                }
                            }}
                        >
                            Cargar Orden
                        </button>
                    </div>
                </div>
            </div>



            <div className="mb-3">
                <label className="form-label">Conductor</label>
                <SelectTwo
                    name="worker_id"
                    id="worker_id"
                    inputValue={sharedFields.worker_id}
                    options={templateData ? SelectTwoMapperOptions(templateData.carriers) : []}
                    hasError={!!fieldHasError('worker_id')}
                    onChange={(val: any) =>
                        setSharedFields({ ...sharedFields, worker_id: val.value })
                    }
                    onFocus={() => onFocusRemove('worker_id')}
                    placeholder="Seleccione conductor"
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('worker_id')}
                    </div>
            </div>

            <div className="mb-3">
                <label className="form-label">Tracto</label>
                <SelectTwo
                    name="truck_id"
                    id="truck_id"
                    inputValue={sharedFields.truck_id}
                    hasError={!!fieldHasError('truck_id')}
                    options={templateData ? SelectTwoMapperOptions(templateData.trucks) : []}
                    onChange={(val: any) =>
                        setSharedFields({ ...sharedFields, truck_id: val.value })
                    }
                    placeholder="Seleccione tracto"
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('truck_id')}
                    </div>
            </div>


            <div className="mb-3">
                <label className="form-label">Semi Remolque</label>
                <SelectTwo
                    name="hopper_id"
                    id="hopper_id"
                    inputValue={sharedFields.hopper_id}
                    hasError={!!fieldHasError('hopper_id')}
                    options={templateData ? SelectTwoMapperOptions(templateData.hoppers) : []}
                    onChange={(val: any) =>
                        setSharedFields({ ...sharedFields, hopper_id: val.value })
                    }
                    placeholder="Seleccione semi remolque"
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('hopper_id')}
                    </div>
            </div>
        </div>


            {beforeSegment && (
                <div className="mb-3 border p-3 rounded bg-light">
                    <h5 className="text-muted">Orden cargada: {beforeSegment.id}</h5>
                    <TransportOrderMultiForm
                        transportOrderForm={beforeSegment}
                        setTransportOrderForm={() => {
                        // Función vacía intencional porque el formulario está deshabilitado (modo solo lectura)
                        }}
                        disabled={true}
                        clients={templateData.clients}
                        products={templateData.products}
                        route_segments={templateData.route_segments}
                        inverseRouteSegments={[]} // si quieres dejarlo vacío
                        carriers={templateData.carriers}
                        trucks={templateData.trucks}
                        hoppers={templateData.hoppers}
                        currenciesExternalRate={templateData.currency_external_rates}
                        unitsExternalRate={templateData.unit_external_rates}
                        currenciesCommissionRate={templateData.currency_commissions}
                        unitsCommissionRate={templateData.unit_commissions}
                        optionTrips={templateData.option_trips}
                        fetchingRouteSegment={false}
                        transport_orders={templateData.transport_orders}
                        previousOrder={beforeSegment?.position ? templateData.transport_orders?.find((to: any) => to.position === beforeSegment.position! - 1) : undefined}
                        index={-1}
                    />
                </div>
            )}




            {transportOrders.map((order, index) => (
                <div className="mb-3 border p-3 rounded bg-light" key={index}>
                    <h5>Tramo #{index + 1}</h5>
                    <TransportOrderMultiForm
                    transportOrderForm={order}
                    setTransportOrderForm={(data) => updateSegment(index, data)}
                    errorFields={errorFields?.[index] || {}}
                    clients={templateData.clients}
                    products={templateData.products}
                    route_segments={templateData.route_segments}
                    inverseRouteSegments={inverseRouteSegmentsList[index] || []}
                    carriers={templateData.carriers}
                    trucks={templateData.trucks}
                    hoppers={templateData.hoppers}
                    currenciesExternalRate={templateData.currency_external_rates}
                    unitsExternalRate={templateData.unit_external_rates}
                    currenciesCommissionRate={templateData.currency_commissions}
                    unitsCommissionRate={templateData.unit_commissions}
                    optionTrips={templateData.option_trips}
                    fetchingRouteSegment={fetchingGetInverseRouteSegmentByRouteSegments}
                    transport_orders={templateData.transport_orders}
                    index={index}
                    />
                    {transportOrders.length > 1 && (
                    <div className="text-end mt-2">
                        <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeSegment(index)}
                        >
                        Eliminar tramo
                        </button>
                    </div>
                    )}
                </div>
                ))}

            {afterSegment && (
                <div className="mb-3 border p-3 rounded bg-light">
                    <h5 className="text-muted">Orden de Transporte final: {afterSegment.id} </h5>
                    <TransportOrderMultiForm
                        transportOrderForm={afterSegment}
                        setTransportOrderForm={() => {
                        // Función vacía intencional porque el formulario está deshabilitado (modo solo lectura)
                        }}
                        disabled={true}
                        clients={templateData.clients}
                        products={templateData.products}
                        route_segments={templateData.route_segments}
                        inverseRouteSegments={[]} // también puede ir vacío
                        carriers={templateData.carriers}
                        trucks={templateData.trucks}
                        hoppers={templateData.hoppers}
                        currenciesExternalRate={templateData.currency_external_rates}
                        unitsExternalRate={templateData.unit_external_rates}
                        currenciesCommissionRate={templateData.currency_commissions}
                        unitsCommissionRate={templateData.unit_commissions}
                        optionTrips={templateData.option_trips}
                        fetchingRouteSegment={false}
                        transport_orders={templateData.transport_orders}
                        index={-2}
                    />
                </div>
            )}




            <div className="text-center mb-3">
                <button className="btn btn-outline-primary" onClick={addNewSegment}>
                    + Agregar otro tramo
                </button>
            </div>

            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={cancel}
                        locked={fetchingStoreTransportOrder}
                    />
                </div>


                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreTransportOrder} />
                </div>
            </div>
        </>
    );
};

export default TransportOrderMultiCreate;
